import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import AdminButton from '../../../Admin/components/AdminButton';
import { CardStatus } from '../configTypes';
import { LOAD_ERROR_MESSAGE } from '../constants';
import ConfigSkeleton from './ConfigSkeleton';

interface ConfigCardProps {
  title: string;
  subtitle?: string;
  /** Top-right action (for example the Edit button). */
  action?: React.ReactNode;
  /** `loading` shows skeletons; `error` shows "Configuration could not be loaded." with Retry. */
  status?: CardStatus;
  onRetry?: () => void;
  /** Table-shaped skeleton while loading, and the body runs edge to edge (a ConfigTable). */
  table?: boolean;
  children: React.ReactNode;
}

/** Card used by every configuration section: header, loading skeleton, error + Retry, body. */
const ConfigCard: React.FC<ConfigCardProps> = ({
  title,
  subtitle,
  action,
  status = 'ready',
  onRetry,
  table,
  children,
}) => (
  <View style={styles.card}>
    <View style={styles.header}>
      <View style={styles.titleGroup}>
        <Text style={styles.title}>{title}</Text>
        {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
      </View>
      {action ? <View style={styles.action}>{action}</View> : null}
    </View>
    {status === 'loading' ? (
      <ConfigSkeleton table={table} />
    ) : status === 'error' ? (
      <View style={styles.error} accessibilityRole="alert">
        <Text style={styles.errorText}>{LOAD_ERROR_MESSAGE}</Text>
        <AdminButton label="Retry" variant="secondary" small onPress={onRetry} />
      </View>
    ) : (
      <View style={table ? styles.flushBody : undefined}>{children}</View>
    )}
  </View>
);

const styles = StyleSheet.create({
  // .card: 24px padding, 8px radius, soft blue elevation
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    padding: 24,
    overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0px 0px 24px rgba(0, 94, 192, 0.08)' },
      default: {
        shadowColor: '#005EC0',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.08,
        shadowRadius: 24,
        elevation: 2,
      },
    }),
  },
  // .card-head: title left, actions right, 16px below
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  titleGroup: { flex: 1 },
  action: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  title: {
    fontSize: 20,
    lineHeight: 28,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 18,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
    marginTop: 2,
  },
  // .cfg-table-pad: the table spans the full card width and sits on the card's bottom edge
  flushBody: { marginHorizontal: -24, marginBottom: -24 },
  // .cfg-error
  error: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FE937A',
    borderLeftWidth: 4,
    borderLeftColor: AdminColors.red500,
    backgroundColor: AdminColors.bgSurface,
  },
  errorText: {
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
});

export default ConfigCard;
